import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Accordion,
} from "react-bootstrap";
import { Calculator, Info, CreditCard, HelpCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";

const planTypes = {
  
};

const PremiumCalculator = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [formData, setFormData] = useState({
    planType: "",
    specificPlan: "",
    age: "",
    sumAssured: "",
    gender: "male",
    smoker: "no",
    occupation: "standard",
    medicalCategory: "standard",
    paymentMode: "yearly",
  });

  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const selectedPlan = (() => {
    const category = planTypes[formData.planType];
    return category?.plans.find((plan) => plan.code === formData.specificPlan);
  })();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setResult(null);
  };

  const validateInputs = () => {
    let newErrors = {};
    if (!formData.planType) newErrors.planType = "Select a plan type";
    if (!formData.specificPlan) newErrors.specificPlan = "Select a specific plan";
    if (!formData.age || isNaN(Number(formData.age))) newErrors.age = "Enter a valid age";
    if (!formData.sumAssured || isNaN(Number(formData.sumAssured)))
      newErrors.sumAssured = "Enter a valid sum assured";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculatePremium = () => {
    if (!validateInputs()) return;

    const age = Number(formData.age);
    const sumAssured = Number(formData.sumAssured);

    if (!selectedPlan) {
      alert(`Please select a valid plan.`);
      return;
    }

    const baseRate = Number(selectedPlan.baseRate);
    if (!baseRate || baseRate <= 0) {
      alert(`Plan "${selectedPlan.name}" is missing a base rate.`);
      return;
    }

    if (age < selectedPlan.minAge || age > selectedPlan.maxAge) {
      alert(`Age must be between ${selectedPlan.minAge} and ${selectedPlan.maxAge}`);
      return;
    }

    if (sumAssured < selectedPlan.minSA) {
      alert(`Minimum Sum Assured is ₹${selectedPlan.minSA.toLocaleString()}`);
      return;
    }

    let ageFactor = age < 30 ? 0.9 : age < 40 ? 1 : 1.2;
    let genderFactor = formData.gender === "female" ? 0.95 : 1;
    let smokerFactor = formData.smoker === "yes" ? 1.5 : 1;

    const occupationFactors = { standard: 1, hazardous: 1.25, medical: 0.95, professional: 0.9 };
    const occupationFactor = occupationFactors[formData.occupation] || 1;

    const medicalFactors = { standard: 1, substandard: 1.3, preferred: 0.85 };
    const medicalFactor = medicalFactors[formData.medicalCategory] || 1;

    const premiumPerThousand =
      baseRate * ageFactor * genderFactor * smokerFactor * occupationFactor * medicalFactor;
    const annualPremium = Math.round((sumAssured / 1000) * premiumPerThousand);

    const modeFactors = { yearly: 1, halfyearly: 0.52, quarterly: 0.27, monthly: 0.09 };
    const premiumByMode = Math.round(annualPremium * (modeFactors[formData.paymentMode] || 1));

    const serviceTax = Math.round(premiumByMode * 0.18);
    const total = premiumByMode + serviceTax;

    setResult({
      premiumByMode,
      serviceTax,
      totalWithTax: total,
      premiumPerThousand: premiumPerThousand.toFixed(2),
      factors: { ageFactor, genderFactor, smokerFactor, occupationFactor, medicalFactor },
    });
  };

  return (
    <div className="dashboard-layout d-flex">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      {/* Main Content */}
      <div className="main-content flex-grow-1">
        <div className="container py-4">
          <h2 className="fw-bold mb-4 d-flex align-items-center">
             Premium Calculator
          </h2>

          <Row className="g-4">
            {/* Form Section */}
            <Col md={6}>
              <Card className="p-4 shadow-sm rounded-4 premium-calculator-card">
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Plan Type <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="planType"
                          value={formData.planType}
                          onChange={(e) =>
                            setFormData({ ...formData, planType: e.target.value, specificPlan: "" })
                          }
                        >
                          <option value="">-- Select Plan Type --</option>
                          {Object.entries(planTypes).map(([key, type]) => (
                            <option key={key} value={key}>
                              {type.name}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.planType && <small className="text-danger">{errors.planType}</small>}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Specific Plan <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="specificPlan"
                          value={formData.specificPlan}
                          onChange={handleChange}
                          disabled={!formData.planType}
                        >
                          <option value="">-- Select Plan --</option>
                          {planTypes[formData.planType]?.plans.map((plan) => (
                            <option key={plan.code} value={plan.code}>
                              {plan.name} ({plan.code})
                            </option>
                          ))}
                        </Form.Select>
                        {errors.specificPlan && (
                          <small className="text-danger">{errors.specificPlan}</small>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {selectedPlan && (
                    <Alert variant="info" className="p-2">
                      <strong>Plan Details:</strong>
                      <br />
                      Age: {selectedPlan.minAge}-{selectedPlan.maxAge} years | Min Sum Assured: ₹
                      {selectedPlan.minSA.toLocaleString()} | Base Rate: {selectedPlan.baseRate}
                    </Alert>
                  )}

                  <Row>
                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Age <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="number" name="age" value={formData.age} onChange={handleChange} />
                        {errors.age && <small className="text-danger">{errors.age}</small>}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                        <Form.Select name="gender" value={formData.gender} onChange={handleChange}>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="">
                    <Form.Label>Sum Assured (₹) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="sumAssured"
                      value={formData.sumAssured}
                      onChange={handleChange}
                    />
                    {errors.sumAssured && <small className="text-danger">{errors.sumAssured}</small>}
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Smoker</Form.Label>
                        <Form.Select name="smoker" value={formData.smoker} onChange={handleChange}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="">
                        <Form.Label>Occupation</Form.Label>
                        <Form.Select
                          name="occupation"
                          value={formData.occupation}
                          onChange={handleChange}
                        >
                          <option value="standard">Standard</option>
                          <option value="professional">Professional</option>
                          <option value="medical">Medical</option>
                          <option value="hazardous">Hazardous</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Medical Category</Form.Label>
                    <Form.Select
                      name="medicalCategory"
                      value={formData.medicalCategory}
                      onChange={handleChange}
                    >
                      <option value="standard">Standard</option>
                      <option value="preferred">Preferred</option>
                      <option value="substandard">Substandard</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Payment Mode</Form.Label>
                    <Form.Select
                      name="paymentMode"
                      value={formData.paymentMode}
                      onChange={handleChange}
                    >
                      <option value="yearly">Yearly</option>
                      <option value="halfyearly">Half-Yearly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="monthly">Monthly</option>
                    </Form.Select>
                  </Form.Group>

                  <div className="d-grid">
                    <Button variant="primary" onClick={calculatePremium}>
                      <Calculator size={16} className="me-2" /> Calculate Premium
                    </Button>
                  </div>
                </Form>
              </Card>
            </Col>

            {/* Premium Summary */}
            <Col md={6}>
              <Card className="p-4 shadow-sm rounded-4">
                <h5>
                  <CreditCard size={18} className="me-2 text-teal-500" /> Premium Summary
                </h5>
                {result ? (
                  <>
                    <Alert variant="success" className="mt-3">
                      <strong>Premium ({formData.paymentMode}):</strong> ₹{result.premiumByMode.toLocaleString()}
                      <br />
                      <strong>GST (18%):</strong> ₹{result.serviceTax.toLocaleString()}
                      <br />
                      <strong>Total:</strong> ₹{result.totalWithTax.toLocaleString()}
                    </Alert>
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          <Info size={16} className="me-2" /> Breakdown & Factors
                        </Accordion.Header>
                        <Accordion.Body>
                          <ul>
                            <li>Premium per ₹1000: ₹{result.premiumPerThousand}</li>
                            <li>Age Factor: {result.factors.ageFactor}</li>
                            <li>Gender Factor: {result.factors.genderFactor}</li>
                            <li>Smoker Factor: {result.factors.smokerFactor}</li>
                            <li>Occupation Factor: {result.factors.occupationFactor}</li>
                            <li>Medical Factor: {result.factors.medicalFactor}</li>
                          </ul>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </>
                ) : (
                  <Alert variant="info" className="mt-3">
                    Fill in details and click Calculate
                  </Alert>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default PremiumCalculator;
